import {
  parseEcommerceDisplayPrice,
  parseEcommerceProductData,
  type EcommerceProductData
} from '../analytics/ecommerce';
import { PRODUCT_DETAIL_PAGE } from '../analytics/product-list-context';
import { QUANTITY_STEPPER_CHANGE_EVENT, mountQuantityStepper } from '../shared/quantity-stepper';
import { mountProductOptionsPanel } from './grouped-pricing-options-block';

const DEFAULT_SUCCESS_TIMEOUT_MS = 1600;
const ADD_TO_CART_TIMEOUT_MS = 15000;
const DESKTOP_BREAKPOINT_PX = 1024;
const ROW_TOP_TOLERANCE_PX = 4;

function resetPriceAlignment(cards: HTMLElement[]): void {
  cards.forEach(card => {
    const priceBlock = card.querySelector<HTMLElement>('.pricing-option-card__price-block');

    if (priceBlock) {
      priceBlock.style.removeProperty('margin-top');
    }
  });
}

function alignPriceBlocks(cards: HTMLElement[]): void {
  resetPriceAlignment(cards);

  if (window.innerWidth < DESKTOP_BREAKPOINT_PX) {
    return;
  }

  const rowGroups = new Map<
    number,
    Array<{ card: HTMLElement; priceBlock: HTMLElement; offset: number }>
  >();

  cards.forEach(card => {
    const priceBlock = card.querySelector<HTMLElement>('.pricing-option-card__price-block');

    if (!priceBlock) {
      return;
    }

    const cardRect = card.getBoundingClientRect();
    const priceRect = priceBlock.getBoundingClientRect();
    const rowKey = Math.round(cardRect.top / ROW_TOP_TOLERANCE_PX) * ROW_TOP_TOLERANCE_PX;
    const rowItems = rowGroups.get(rowKey) ?? [];

    rowItems.push({
      card,
      priceBlock,
      offset: priceRect.top - cardRect.top
    });

    rowGroups.set(rowKey, rowItems);
  });

  rowGroups.forEach(rowItems => {
    const maxOffset = Math.max(...rowItems.map(item => item.offset));

    rowItems.forEach(item => {
      const extraOffset = maxOffset - item.offset;

      if (extraOffset > 0) {
        item.priceBlock.style.marginTop = `${extraOffset}px`;
      }
    });
  });
}

function mountPricingOptionCard(card: HTMLElement): void {
  if (card.dataset.pricingOptionMounted === 'true') return;

  const initialAddButton = card.querySelector<HTMLButtonElement>('[data-add-to-cart]');
  const initialButtonLabel = card.querySelector<HTMLElement>('[data-button-label]');
  const initialFeedback = card.querySelector<HTMLElement>('[data-pricing-option-feedback]');
  const initialStatus = card.querySelector<HTMLElement>('[data-pricing-option-status]');
  const variantSelects = Array.from(
    card.querySelectorAll<HTMLSelectElement>('[data-variant-selects] select')
  );

  const initialStepper = mountQuantityStepper(card, { clearIcon: true });

  if (!initialStepper || !initialAddButton || !initialButtonLabel) {
    return;
  }

  card.dataset.pricingOptionMounted = 'true';

  let addButton = initialAddButton;
  let buttonLabel = initialButtonLabel;
  let feedback = initialFeedback;
  let status = initialStatus;
  let stepper = initialStepper;

  let planName = card.dataset.planName?.trim() || 'item';
  const shouldFail = card.dataset.simulateError === 'true';
  const errorMessage =
    card.dataset.errorMessage?.trim() || 'Unable to add this pricing option right now.';
  let defaultLabel = buttonLabel.textContent?.trim() || 'Add to cart';
  const ecommerceJson = card.querySelector<HTMLScriptElement>(
    '.pricing-option-card__ecommerce-data'
  )?.textContent;

  let successTimeout: number | null = null;

  const refreshActionControls = (): boolean => {
    const nextAddButton = card.querySelector<HTMLButtonElement>('[data-add-to-cart]');
    const nextButtonLabel = card.querySelector<HTMLElement>('[data-button-label]');
    const nextStepper = mountQuantityStepper(card, { clearIcon: true });

    if (!nextAddButton || !nextButtonLabel || !nextStepper) return false;

    addButton = nextAddButton;
    buttonLabel = nextButtonLabel;
    feedback = card.querySelector<HTMLElement>('[data-pricing-option-feedback]');
    status = card.querySelector<HTMLElement>('[data-pricing-option-status]');
    stepper = nextStepper;
    planName = card.dataset.planName?.trim() || 'item';
    defaultLabel = buttonLabel?.textContent?.trim() || 'Add to cart';

    return Boolean(stepper && addButton && buttonLabel);
  };

  const resolveIdentity = (): { parentSku?: string; sku?: string } => {
    if (variantSelects.length === 0) {
      return {
        sku: card.dataset.sku?.trim(),
        parentSku: card.dataset.parentSku?.trim() || undefined
      };
    }

    const selectedOptions = variantSelects.map(select => select.selectedOptions[0]);
    if (selectedOptions.some(option => !option?.value)) {
      return {};
    }

    const selected = selectedOptions[selectedOptions.length - 1];
    return {
      sku: selected?.dataset.sku?.trim() || selected?.value.trim(),
      parentSku: selected?.dataset.parentSku?.trim() || undefined
    };
  };

  const clearFeedback = (): void => {
    if (feedback) {
      feedback.hidden = true;
      feedback.textContent = '';
      feedback.removeAttribute('data-feedback-state');
    }

    addButton.removeAttribute('data-feedback-state');
  };

  const resetButtonUi = (): void => {
    const identity = resolveIdentity();
    addButton.disabled = !identity.sku;
    addButton.removeAttribute('data-feedback-state');
    buttonLabel.textContent = defaultLabel;
  };

  const swapVariantPresentation = (sku: string): boolean => {
    const template = Array.from(
      card.querySelectorAll<HTMLTemplateElement>('template[data-pricing-option-variant]')
    ).find(candidate => candidate.dataset.sku === sku);
    const presentation = card.querySelector<HTMLElement>('[data-pricing-option-presentation]');
    const actions = card.querySelector<HTMLElement>('[data-pricing-option-actions]');

    if (!template || !presentation || !actions) return false;

    const fragment = template.content.cloneNode(true) as DocumentFragment;
    const nextPresentation = fragment.querySelector<HTMLElement>(
      '[data-pricing-option-presentation-template]'
    );
    const nextActions = fragment.querySelector<HTMLElement>(
      '[data-pricing-option-actions-template]'
    );

    if (!nextPresentation || !nextActions) return false;

    presentation.replaceChildren(...Array.from(nextPresentation.childNodes));
    actions.replaceChildren(...Array.from(nextActions.childNodes));

    for (const [name, value] of Object.entries(template.dataset)) {
      if (value) card.dataset[name] = value;
    }

    return refreshActionControls();
  };

  const syncVariantSelection = (): void => {
    const { parentSku, sku } = resolveIdentity();
    const selected = variantSelects[variantSelects.length - 1]?.selectedOptions[0];
    const didSwapPresentation = sku ? swapVariantPresentation(sku) : false;

    if (!didSwapPresentation && selected?.dataset.displayPrice) {
      const priceAmount = card.querySelector<HTMLElement>('.pricing-option-card__price-amount');
      if (priceAmount) priceAmount.textContent = selected.dataset.displayPrice;
    }

    if (sku) {
      card.dataset.sku = sku;
    } else {
      card.removeAttribute('data-sku');
    }

    if (parentSku) {
      card.dataset.parentSku = parentSku;
    } else if (variantSelects.length > 0) {
      card.removeAttribute('data-parent-sku');
    }

    clearFeedback();
    resetButtonUi();
  };

  const scheduleReset = (): void => {
    if (successTimeout !== null) {
      window.clearTimeout(successTimeout);
    }

    successTimeout = window.setTimeout(() => {
      stepper.reset();
      resetButtonUi();
      successTimeout = null;
    }, DEFAULT_SUCCESS_TIMEOUT_MS);
  };

  card.addEventListener(QUANTITY_STEPPER_CHANGE_EVENT, clearFeedback);
  card.querySelector<HTMLInputElement>('[data-quantity-value]')?.addEventListener('input', () => {
    clearFeedback();
  });
  variantSelects.forEach(select => select.addEventListener('change', syncVariantSelection));
  syncVariantSelection();

  card.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-add-to-cart]')) return;

    clearFeedback();

    const { parentSku, sku } = resolveIdentity();

    if (!sku || shouldFail) {
      addButton.setAttribute('data-feedback-state', 'error');

      if (feedback) {
        feedback.hidden = false;
        feedback.dataset.feedbackState = 'error';
        feedback.textContent = errorMessage;
      }

      if (status) {
        status.textContent = `Unable to add ${planName} to cart.`;
      }

      resetButtonUi();
      return;
    }

    addButton.disabled = true;
    addButton.dataset.feedbackState = 'loading';

    const requestId = `${sku}-${Date.now()}`;
    const parsedProduct =
      ecommerceJson && variantSelects.length === 0
        ? parseEcommerceProductData(ecommerceJson)
        : buildPricingFallbackProduct(card, sku, planName);
    const ecommerceProduct = parsedProduct
      ? {
          ...parsedProduct,
          item: {
            ...parsedProduct.item,
            item_list_id: PRODUCT_DETAIL_PAGE.id,
            item_list_name: PRODUCT_DETAIL_PAGE.name
          }
        }
      : null;

    const onResult = (e: Event): void => {
      const evt = e as CustomEvent<{
        requestId: string;
        success: boolean;
        cartCount?: number;
        error?: string;
      }>;
      if (evt.detail.requestId !== requestId) return;

      window.clearTimeout(loadTimeout);
      document.removeEventListener('bentley:add-to-cart-result', onResult);

      if (evt.detail.success) {
        addButton.dataset.feedbackState = 'success';
        buttonLabel.textContent = 'Added to cart';

        if (status) {
          status.textContent = `Added ${planName} to cart.`;
        }

        scheduleReset();
      } else {
        addButton.disabled = false;
        addButton.removeAttribute('data-feedback-state');

        if (feedback) {
          feedback.hidden = false;
          feedback.dataset.feedbackState = 'error';
          feedback.textContent = evt.detail.error || errorMessage;
        }

        if (status) {
          status.textContent = `Unable to add ${planName} to cart.`;
        }
      }
    };

    document.addEventListener('bentley:add-to-cart-result', onResult);

    const loadTimeout = window.setTimeout(() => {
      document.removeEventListener('bentley:add-to-cart-result', onResult);
      addButton.disabled = false;
      addButton.removeAttribute('data-feedback-state');

      if (feedback) {
        feedback.hidden = false;
        feedback.dataset.feedbackState = 'error';
        feedback.textContent = errorMessage;
      }

      if (status) {
        status.textContent = `Unable to add ${planName} to cart.`;
      }
    }, ADD_TO_CART_TIMEOUT_MS);

    const quantity = stepper.getQuantity();
    const detail = {
      requestId,
      sku,
      parentSku,
      quantity,
      planName,
      ecommerceProduct
    };

    document.dispatchEvent(
      new CustomEvent('bentley:add-to-cart-request', {
        detail
      })
    );
  });
}

function buildPricingFallbackProduct(
  card: HTMLElement,
  sku: string | undefined,
  planName: string
): EcommerceProductData | null {
  if (!sku) return null;
  const priceText = card.querySelector('.pricing-option-card__price-amount')?.textContent ?? '';
  const normalizedPrice = parseEcommerceDisplayPrice(priceText);
  const currency = card.querySelector('.pricing-option-card__price-currency')?.textContent?.trim();

  return {
    currency,
    item: {
      item_id: sku,
      item_name: planName,
      item_list_id: PRODUCT_DETAIL_PAGE.id,
      item_list_name: PRODUCT_DETAIL_PAGE.name,
      quantity: 1,
      ...(normalizedPrice && { price: normalizedPrice })
    }
  };
}

export default function pricingOptionsBlock(root: HTMLElement): void {
  const cards = Array.from(root.querySelectorAll<HTMLElement>('[data-pricing-option-card]')).filter(
    card => card.closest<HTMLElement>('[data-module]') === root
  );

  root.querySelectorAll<HTMLElement>('[data-product-options-panel]').forEach(panel => {
    if (panel.closest<HTMLElement>('[data-module]') === root) mountProductOptionsPanel(panel);
  });

  if (cards.length === 0) {
    const hasInfoCards = root.querySelector('.pricing-options-block__info-card-item');
    const hasOptionsPanel = root.querySelector('[data-product-options-panel]');
    if (!hasInfoCards && !hasOptionsPanel) {
      root.hidden = true;
    }
    return;
  }

  cards.forEach(card => {
    mountPricingOptionCard(card);
  });

  const syncPriceAlignment = (): void => {
    window.requestAnimationFrame(() => {
      alignPriceBlocks(cards);
    });
  };

  syncPriceAlignment();
  window.addEventListener('resize', syncPriceAlignment);

  if (document.fonts?.ready) {
    void document.fonts.ready.then(syncPriceAlignment);
  }
}
